/**
 * Risoluzione della scheda lead del chiamante per le chiamate in ingresso.
 *
 * Serve a GAIA-2719: il pop-up "Chiamata da contatto salvato" viveva solo nella
 * window della barra SIP e solo dopo la risposta, quindi l'operatore che lavora
 * nelle view non lo vedeva mai. La lookup viene fatta qui, allo squillo, dalla
 * finestra owner (una sola volta per chiamata) e il risultato viene propagato
 * alle view sul canale `gaia-sip`.
 *
 * NON viene applicata alcuna normalizzazione al numero: il CallerID inbound
 * arriva già in E.164 (`+39…`) ed è il formato con cui il BE risolve i lead
 * (`profile-db."Invitation".phone` è E.164 nel 99,8% dei record non vuoti).
 */

import { getInvitationInfoFromPhoneNumber } from "@/api/apiclient/lead";

/** Forma minima della risposta del BE per `GET /gaia/leads/phone/{numero}`. */
export interface IncomingLeadLookupResponse {
  status?: unknown;
  id?: number | string | null;
}

/** Firma della lookup, iniettabile nei test. */
export type LeadLookupFetcher = (
  phoneNumber: string
) => Promise<{ data?: IncomingLeadLookupResponse | null }>;

/**
 * Costruisce l'URL della scheda lead dalla risposta del BE.
 *
 * Ritorna `null` quando il numero non corrisponde a nessun lead: il BE in quel
 * caso risponde 200 con un payload privo di `status`/`id`, non un 404.
 * Funzione pura.
 */
export function buildLeadTargetUrl(
  data: IncomingLeadLookupResponse | null | undefined
): string | null {
  if (!data) return null;
  if (!data.status) return null;
  if (data.id === null || data.id === undefined) return null;

  const id = String(data.id).trim();
  // "undefined"/"null" come stringa: già visti a DB come entity_id di azioni
  // orfane scritte da trackCallEvent. Non mandiamo l'operatore su /lead/edit/undefined.
  if (!id || id === "undefined" || id === "null") return null;

  return `/lead/edit/${id}`;
}

/**
 * Risolve l'URL della scheda del lead che possiede `phoneNumber`.
 *
 * Fail-fast sull'input e NON inghiotte gli errori del BE: la degradazione è una
 * decisione del call site, che sa se la scheda è un arricchimento opzionale
 * (allo squillo lo è: la notifica di chiamata è già partita).
 *
 * @throws se `phoneNumber` è vuoto, o se la chiamata al BE fallisce.
 */
export async function resolveIncomingLeadUrl(
  phoneNumber: string,
  fetcher: LeadLookupFetcher = getInvitationInfoFromPhoneNumber
): Promise<string | null> {
  if (!phoneNumber || !phoneNumber.trim()) {
    throw new Error("resolveIncomingLeadUrl: phoneNumber is required");
  }

  const response = await fetcher(phoneNumber);
  return buildLeadTargetUrl(response?.data);
}
